import type { VideoListItem } from '../../types/videos';
import { TranscriptionJobBadge } from './TranscriptionJobBadge';
import { PostLink } from './PostLink';

function formatLanguage(video: VideoListItem): string {
  if (video.language == null) return '—';
  const hasTranslation = video.available_languages.some(l => l !== video.language);
  return video.language.toUpperCase() + (hasTranslation ? ' +EN' : '');
}

interface VideoTableProps {
  videos: VideoListItem[];
  onTranscribe: (stem: string) => void;
  onRowClick: (stem: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m < 60) return `${m}:${s.toString().padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}:${rm.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export function VideoTable({ videos, onTranscribe, onRowClick }: VideoTableProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Mobile card list */}
      <div className="block sm:hidden divide-y divide-gray-100">
        {videos.map((video) => (
          <div key={video.stem} className="relative">
            <button
              onClick={() => onRowClick(video.stem)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <img
                  src={`/api/videos/${video.stem}/thumbnail`}
                  alt=""
                  className="w-20 h-14 object-cover rounded bg-gray-100 shrink-0"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-gray-900 truncate">{video.title}</div>
                  <div className="font-mono text-xs text-gray-500 truncate">{video.filename}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatBytes(video.size)} · {formatDuration(video.duration_seconds)} · {relativeTime(video.mtime)} · {formatLanguage(video)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {video.tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{tag}</span>
                    ))}
                    {video.has_transcript && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">transcript</span>
                    )}
                    {video.has_caption && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">caption</span>
                    )}
                    <TranscriptionJobBadge status={video.job_status} />
                    <TranscriptionJobBadge status={video.translation_job_status} />
                  </div>
                </div>
              </div>
            </button>
            <div className="px-4 pb-3">
              <PostLink postUrl={video.post_url} />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <table className="hidden sm:table w-full text-sm text-left">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="px-4 py-3 font-medium">Video</th>
            <th className="px-4 py-3 font-medium">Size</th>
            <th className="px-4 py-3 font-medium">Duration</th>
            <th className="px-4 py-3 font-medium">Modified</th>
            <th className="px-4 py-3 font-medium">Lang</th>
            <th className="px-4 py-3 font-medium">Tags</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {videos.map((video) => (
            <tr
              key={video.stem}
              onClick={() => onRowClick(video.stem)}
              className="hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <img
                    src={`/api/videos/${video.stem}/thumbnail`}
                    alt=""
                    className="w-16 h-10 object-cover rounded bg-gray-100 shrink-0"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{video.title}</div>
                    <div className="font-mono text-xs text-gray-500 truncate">{video.filename}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-gray-500">{formatBytes(video.size)}</td>
              <td className="px-4 py-3 text-gray-500">{formatDuration(video.duration_seconds)}</td>
              <td className="px-4 py-3 text-gray-500">{relativeTime(video.mtime)}</td>
              <td className="px-4 py-3 text-gray-500">{formatLanguage(video)}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {video.tags.map(tag => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{tag}</span>
                  ))}
                  {video.tags.length === 0 && <span className="text-xs text-gray-400">—</span>}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {video.has_transcript && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">transcript</span>
                  )}
                  {video.has_caption && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">caption</span>
                  )}
                  <TranscriptionJobBadge status={video.job_status} />
                  <TranscriptionJobBadge status={video.translation_job_status} />
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onTranscribe(video.stem); }}
                    disabled={video.job_status === 'queued' || video.job_status === 'running'}
                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Transcribe
                  </button>
                  <span onClick={(e) => e.stopPropagation()}>
                    <PostLink postUrl={video.post_url} />
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
